import { useEffect, useState } from 'react';
import type { FeedbackRecord } from '@feedback-system/types';
import { fetchFeedbackDetail, updateFeedback, getScreenshotUrl } from '../api';
import { StatusBadge } from '../components/StatusBadge';
import { SeverityBadge } from '../components/SeverityBadge';
import { ProviderBadge } from '../components/ProviderBadge';
import { ScreenshotPreview } from '../components/ScreenshotPreview';

interface FeedbackDetailProps {
  feedbackId: string;
  onBack: () => void;
}

export function FeedbackDetail({ feedbackId, onBack }: FeedbackDetailProps) {
  const [feedback, setFeedback] = useState<FeedbackRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const loadDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchFeedbackDetail(feedbackId);
        setFeedback(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load feedback');
      } finally {
        setLoading(false);
      }
    };

    loadDetail();
  }, [feedbackId]);

  const handleStatusChange = async (newStatus: string) => {
    if (!feedback || updating) return;

    try {
      setUpdating(true);
      const updated = await updateFeedback(feedbackId, { status: newStatus });
      setFeedback(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleReanalyze = async () => {
    if (!feedback || updating) return;

    try {
      setUpdating(true);
      const updated = await updateFeedback(feedbackId, { reanalyze: true });
      setFeedback(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to re-analyze');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="card text-center py-12">
        <p className="text-dark-200">Loading feedback...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="btn-secondary">
          ← Back
        </button>
        <div className="card bg-accent-red/10 border border-accent-red/30">
          <p className="text-accent-red text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="btn-secondary">
          ← Back
        </button>
        <div className="card text-center py-12">
          <p className="text-dark-200">Feedback not found</p>
        </div>
      </div>
    );
  }

  const date = new Date(feedback.createdAt);
  const dateStr = date.toLocaleDateString() + ' ' + date.toLocaleTimeString();

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="btn-secondary">
        ← Back
      </button>

      <div className="card space-y-6">
        {/* Header */}
        <div className="border-b border-dark-700 pb-4">
          <div className="flex items-start justify-between mb-3">
            <h1 className="text-2xl font-bold text-dark-100 flex-1">{feedback.description}</h1>
            <StatusBadge status={feedback.status} />
          </div>
          <p className="text-sm text-dark-200 mb-3">{dateStr}</p>
          <a href={feedback.url} target="_blank" rel="noopener noreferrer" className="text-accent-primary text-sm hover:underline break-all">
            {feedback.url}
          </a>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-2">
          <SeverityBadge severity={feedback.severity} />
          <ProviderBadge provider={feedback.aiProvider} status={feedback.aiStatus} />
        </div>

        {/* Details */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-dark-200 font-medium">Project ID</p>
            <p className="text-dark-100">{feedback.projectId}</p>
          </div>
          {feedback.testerName && (
            <div>
              <p className="text-dark-200 font-medium">Tester Name</p>
              <p className="text-dark-100">{feedback.testerName}</p>
            </div>
          )}
          {feedback.testerEmail && (
            <div>
              <p className="text-dark-200 font-medium">Tester Email</p>
              <p className="text-dark-100">{feedback.testerEmail}</p>
            </div>
          )}
          {feedback.elementSelector && (
            <div>
              <p className="text-dark-200 font-medium">Element Selector</p>
              <p className="text-dark-100 font-mono text-xs break-all">{feedback.elementSelector}</p>
            </div>
          )}
        </div>

        {/* Screenshot */}
        {feedback.screenshotUrl && (
          <div>
            <h2 className="text-lg font-bold text-dark-100 mb-3">Screenshot</h2>
            <ScreenshotPreview url={getScreenshotUrl(feedback.id)} />
          </div>
        )}

        {/* AI Analysis */}
        {feedback.aiStatus !== 'skipped' && (
          <div className="border-t border-dark-700 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-dark-100">AI Analysis</h2>
              {feedback.aiStatus === 'completed' && (
                <button onClick={handleReanalyze} disabled={updating} className="btn-secondary text-xs">
                  {updating ? 'Analyzing...' : 'Re-analyze'}
                </button>
              )}
            </div>

            {feedback.aiStatus === 'pending' && (
              <p className="text-dark-200 text-sm">Analyzing feedback...</p>
            )}

            {feedback.aiStatus === 'completed' && (
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-dark-200 mb-1">Category</p>
                  <p className="text-dark-100">{feedback.aiCategory ? feedback.aiCategory.charAt(0).toUpperCase() + feedback.aiCategory.slice(1) : 'Unknown'}</p>
                </div>
                {feedback.aiSummary && (
                  <div>
                    <p className="text-xs font-medium text-dark-200 mb-1">Summary</p>
                    <p className="text-dark-100">{feedback.aiSummary}</p>
                  </div>
                )}
                {feedback.aiSuggestedFix && (
                  <div>
                    <p className="text-xs font-medium text-dark-200 mb-1">Suggested Fix</p>
                    <p className="text-dark-100">{feedback.aiSuggestedFix}</p>
                  </div>
                )}
              </div>
            )}

            {feedback.aiStatus === 'failed' && (
              <div className="bg-accent-red/10 border border-accent-red/30 rounded p-3">
                <p className="text-accent-red text-sm">{feedback.aiError || 'Analysis failed'}</p>
              </div>
            )}
          </div>
        )}

        {/* Status Update */}
        <div className="border-t border-dark-700 pt-6">
          <label className="block text-sm font-medium text-dark-200 mb-2">Update Status</label>
          <select
            value={feedback.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={updating}
            className="select"
          >
            <option value="open">Open</option>
            <option value="in_review">In Review</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>
    </div>
  );
}
